import { WORKSPACE_PERSONA_ROLES, type WorkspacePersonaRole } from '@myplayer/rbac';
import type { OrganisationDocument } from '@myplayer/schema';
import * as ui from '@myplayer/ui';
import { useEffect, useRef, useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  applyThemePreference,
  getRoleChipVariant,
  syncSystemThemeWithBrandOverrides,
  type AdminThemePreference,
} from './App.js';
import * as authSession from './auth-session.js';
import {
  type AdminUserSession,
  type WorkspaceOption,
  type WorkspaceScopeMode,
} from './auth-session.js';
import { ChangePasswordModal } from './auth-screens.js';
import { Badge, Button, ToastNotification } from './components/ui/primitives.js';
import { isProductionBuild } from './firebase.js';
import { formatOrganisationName } from './organisation-firestore.js';
import { ADMIN_STRINGS, formatPersonaRoleLabel } from './strings.js';

const THEME_STORAGE_KEY = 'mp_admin_theme';
const ICON_BTN_CLASS = 'h-10 w-10 min-h-10 max-h-10 rounded-full text-fg [&_svg]:h-5 [&_svg]:w-5';
const WORKSPACE_ROW_CLS = (active: boolean): string =>
  `flex w-full cursor-pointer items-center justify-between gap-3 px-4 py-2 text-left text-[13px] transition-colors ${active ? 'bg-brand-wash font-semibold text-brand' : 'text-fg hover:bg-surface-subtle'}`;
const AVATAR_BADGE_CLS =
  'flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-subtle bg-surface-muted text-[11px] font-semibold text-fg';

function renderSuiteAppIcon(appId: string | undefined): ReactElement {
  if (appId === 'projects') return <ui.IconAppProjects className="h-[26px] w-[26px]" />;
  if (appId === 'create') return <ui.IconAppCreate className="h-[26px] w-[26px]" />;
  if (appId === 'account') return <ui.IconAppAccount className="h-[26px] w-[26px]" />;
  return <ui.IconQrCode className="h-[26px] w-[26px]" />;
}

function renderWorkspaceChoiceButton(
  key: string,
  testId: string,
  active: boolean,
  init: string,
  label: string,
  onPick: () => void,
): ReactElement {
  return (
    <Button
      key={key}
      type="button"
      variant="ghost"
      size="inline"
      data-testid={testId}
      onClick={onPick}
      className={WORKSPACE_ROW_CLS(active)}
    >
      <span className="flex min-w-0 items-center gap-3">
        <span className={AVATAR_BADGE_CLS}>{init}</span>
        <span className="truncate">{label}</span>
      </span>
      {active && <span className="h-2 w-2 shrink-0 rounded-full bg-brand" />}
    </Button>
  );
}

export interface AdminTopNavProps {
  readonly session: AdminUserSession;
  readonly organisation?: OrganisationDocument | null;
  readonly availableWorkspaces: readonly WorkspaceOption[];
  readonly workspaceScope?: WorkspaceScopeMode;
  readonly setWorkspaceScope?: (scope: WorkspaceScopeMode) => void;
  readonly switchWorkspace: (workspaceId: string) => void;
  readonly switchPersonaRole: (personaRole: WorkspacePersonaRole) => void;
  readonly signOut: () => Promise<void>;
}

export function AdminTopNav(props: AdminTopNavProps): ReactElement {
  const { session, organisation, availableWorkspaces, setWorkspaceScope, switchWorkspace } = props;
  const workspaceScope = props.workspaceScope ?? 'WORKSPACE';
  const navigate = useNavigate();
  const [themePref, setThemePref] = useState<AdminThemePreference>(() => {
    const raw =
      typeof window !== 'undefined' ? window.localStorage.getItem(THEME_STORAGE_KEY) : null;
    return raw === 'dark' || raw === 'light' ? raw : 'system';
  });
  const [appsMenuOpen, setAppsMenuOpen] = useState(false);
  const [orgMenuOpen, setOrgMenuOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [passwordSavedMessage, setPasswordSavedMessage] = useState<string | null>(null);
  const appsMenuRef = useRef<HTMLDivElement | null>(null);
  const orgMenuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    syncSystemThemeWithBrandOverrides();
    applyThemePreference(themePref);
  }, [themePref]);

  const appsDismiss = () => {
    setAppsMenuOpen(false);
    setOrgMenuOpen(false);
  };
  useEffect(
    () =>
      authSession.registerOutsideMenusDismiss(
        appsMenuOpen || orgMenuOpen,
        [appsMenuRef, orgMenuRef],
        appsDismiss,
      ),
    [appsMenuOpen, orgMenuOpen],
  );

  const handleToggleTheme = (): void => {
    const next = authSession.computeNextAdminTheme(themePref);
    setThemePref(next);
    if (typeof window !== 'undefined') window.localStorage.setItem(THEME_STORAGE_KEY, next);
    applyThemePreference(next);
  };

  const activeWs = availableWorkspaces.find((ws) => ws.workspaceId === session.activeWorkspaceId);
  const wsDisplayName = activeWs?.name || session.activeWorkspaceId;
  const orgDisplayName = formatOrganisationName(organisation?.name, session.orgId);
  const canViewAllWorkspaces = authSession.canUseAllWorkspacesScope(session, availableWorkspaces);
  const isAllWorkspacesMode = workspaceScope === 'ALL' && canViewAllWorkspaces;
  const wsInitials = isAllWorkspacesMode
    ? 'All'
    : wsDisplayName.replace(/^ws_/i, '').slice(0, 2).toUpperCase();

  const selectChoice = (selectedValue: string): void => {
    if (selectedValue === authSession.ALL_WORKSPACES_SCOPE_VALUE) {
      if (setWorkspaceScope) setWorkspaceScope('ALL');
      else switchWorkspace(authSession.ALL_WORKSPACES_SCOPE_VALUE);
    } else {
      setWorkspaceScope?.('WORKSPACE');
      switchWorkspace(selectedValue);
    }
    setOrgMenuOpen(false);
  };
  const themeLabel =
    authSession.computeNextAdminTheme(themePref) === 'light' ? 'Light mode' : 'Dark mode';

  return (
    <header
      data-testid="workspace-identity-card"
      className="sticky top-0 z-30 flex h-[56px] w-full shrink-0 items-center justify-between gap-3 border-b border-border bg-surface-frosted px-4 shadow-card-base backdrop-blur-md sm:px-6"
    >
      <div className="flex min-w-0 items-center gap-1.5">
        <Button
          type="button"
          variant="ghost"
          size="inline"
          onClick={() => navigate('/overview')}
          className="inline-flex min-w-0 cursor-pointer items-center gap-2 rounded-card px-1.5 py-1 text-left transition-colors hover:bg-surface-subtle [&_svg]:h-7 [&_svg]:w-7"
        >
          <ui.IconMyPlayerLogo size={28} className="shrink-0" />
          <span className="whitespace-nowrap text-[16px] font-bold tracking-[-0.2px] text-fg">
            {ui.DESIGN_TOKENS.brandIdentity.logoText}
          </span>
        </Button>
        <span aria-hidden="true" className="select-none font-normal text-fg-muted">
          /
        </span>
        <span
          data-testid="active-organisation-badge"
          title={session.orgId}
          className="hidden truncate px-1 text-[13px] font-medium text-fg-muted sm:inline"
        >
          {orgDisplayName}
        </span>
        <span aria-hidden="true" className="hidden select-none font-normal text-fg-muted sm:inline">
          /
        </span>
        <span
          data-testid="active-workspace-badge"
          title={
            isAllWorkspacesMode ? `${orgDisplayName} (All workspaces)` : session.activeWorkspaceId
          }
          className="truncate px-1.5 text-[14px] font-semibold text-fg-secondary"
        >
          <span>{isAllWorkspacesMode ? 'All workspaces' : wsDisplayName}</span>
          {!isAllWorkspacesMode && wsDisplayName !== session.activeWorkspaceId && (
            <span className="sr-only"> ({session.activeWorkspaceId})</span>
          )}
        </span>
      </div>

      <div className="flex shrink-0 items-center gap-1.5">
        <span data-testid="active-role-badge" className="sr-only">
          {formatPersonaRoleLabel(session.personaRole)}
        </span>

        <div ref={appsMenuRef} className="relative">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="MyPlayer Apps"
            title="MyPlayer Apps"
            aria-expanded={appsMenuOpen}
            onClick={() => {
              setAppsMenuOpen((p) => !p);
              setOrgMenuOpen(false);
            }}
            className={`${ICON_BTN_CLASS} ${appsMenuOpen ? 'text-brand' : ''}`}
          >
            <ui.IconApps />
          </Button>
          {appsMenuOpen && (
            <div
              role="menu"
              aria-label="MyPlayer Apps"
              className="absolute right-0 top-full z-50 mt-2 flex flex-row gap-3.5 rounded-[16px] border border-subtle bg-surface px-4.5 py-4 shadow-modal"
            >
              {authSession.MYPLAYER_APPS.map((raw) => {
                const [id, name, href] = raw.split('|');
                return (
                  <a
                    key={id}
                    href={href}
                    onClick={(e) => {
                      if (id !== 'resolver') return;
                      e.preventDefault();
                      setAppsMenuOpen(false);
                      navigate('/overview');
                    }}
                    className="group flex w-[76px] flex-col items-center justify-start rounded-[14px] p-1 text-center transition-all"
                  >
                    <span className="flex h-14 w-14 items-center justify-center rounded-[14px] border border-subtle bg-surface-subtle text-fg transition-all group-hover:-translate-y-0.5 group-hover:border-border group-hover:bg-surface-muted [&_svg]:h-[26px] [&_svg]:w-[26px]">
                      {renderSuiteAppIcon(id)}
                    </span>
                    <span className="mt-2 max-w-full truncate text-[13px] font-medium leading-tight tracking-[-0.01em] text-fg">
                      {name}
                    </span>
                    <span
                      className={`mt-1.5 h-1 w-1 rounded-full transition-colors ${id === 'resolver' ? 'bg-brand' : 'bg-transparent'}`}
                    />
                  </a>
                );
              })}
            </div>
          )}
        </div>

        <div ref={orgMenuRef} className="relative">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Account, workspace, and admin role menu"
            title={session.email ?? wsDisplayName}
            aria-expanded={orgMenuOpen}
            data-testid="admin-profile-menu-trigger"
            onClick={() => {
              setOrgMenuOpen((prev) => !prev);
              setAppsMenuOpen(false);
            }}
            className="h-10 w-10 min-h-10 max-h-10 rounded-full"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-full border-[1.5px] border-border bg-surface-muted text-[13px] font-semibold tracking-[-0.02em] text-fg">
              {wsInitials}
            </span>
          </Button>
          {orgMenuOpen && (
            <div
              role="dialog"
              aria-label="Workspace and admin persona switcher"
              data-testid="admin-profile-popover"
              className="absolute right-0 top-full z-50 mt-2 w-[304px] overflow-hidden rounded-[12px] border border-subtle bg-surface shadow-modal"
            >
              <div className="flex flex-col items-center border-b border-subtle bg-surface-subtle px-5 pt-6 pb-5 text-center">
                <div className="mb-3 flex h-[68px] w-[68px] items-center justify-center rounded-full border border-subtle bg-surface-muted text-[20px] font-semibold tracking-[-0.02em] text-fg">
                  {wsInitials}
                </div>
                <div className="max-w-full truncate text-[15px] font-bold text-fg">
                  {isAllWorkspacesMode ? orgDisplayName : wsDisplayName}
                </div>
                <div
                  data-testid="profile-organisation-name"
                  className="mt-0.5 max-w-full truncate text-[11px] font-medium text-fg-muted"
                >
                  {isAllWorkspacesMode ? 'All workspaces' : orgDisplayName}
                </div>
                {session.email !== null && (
                  <div className="mt-0.5 max-w-full truncate text-[12px] text-fg-secondary">
                    {session.email}
                  </div>
                )}
                <div className="mt-2.5">
                  <Badge variant={getRoleChipVariant(session.personaRole)}>
                    {formatPersonaRoleLabel(session.personaRole)}
                  </Badge>
                </div>
              </div>

              {canViewAllWorkspaces && (
                <div className="border-b border-subtle py-2">
                  <div className="px-4 py-1 text-[11px] font-semibold text-fg-muted">
                    Organisation ({orgDisplayName})
                  </div>
                  {renderWorkspaceChoiceButton(
                    'all',
                    'profile-switch-all-workspaces',
                    isAllWorkspacesMode,
                    'All',
                    'All workspaces',
                    () => selectChoice(authSession.ALL_WORKSPACES_SCOPE_VALUE),
                  )}
                </div>
              )}

              {availableWorkspaces.length > 0 && (
                <div className="border-b border-subtle py-2">
                  <div className="px-4 py-1 text-[11px] font-semibold text-fg-muted">
                    Workspaces ({availableWorkspaces.length})
                  </div>
                  <div className="max-h-40 overflow-y-auto">
                    {availableWorkspaces.map((ws) =>
                      renderWorkspaceChoiceButton(
                        ws.workspaceId,
                        `profile-switch-workspace-${ws.workspaceId}`,
                        !isAllWorkspacesMode && ws.workspaceId === session.activeWorkspaceId,
                        ws.name.replace(/^ws_/i, '').slice(0, 2).toUpperCase(),
                        ws.name,
                        () => selectChoice(ws.workspaceId),
                      ),
                    )}
                  </div>
                </div>
              )}

              {!isProductionBuild() && (
                <div className="border-b border-subtle py-2">
                  <div className="px-4 py-1 text-[11px] font-semibold text-fg-muted">
                    Admin Persona Switcher
                  </div>
                  <div className="space-y-0.5 px-2">
                    {WORKSPACE_PERSONA_ROLES.map((roleOpt) => (
                      <Button
                        key={roleOpt}
                        type="button"
                        variant="ghost"
                        size="inline"
                        data-testid={`switch-persona-${roleOpt}`}
                        onClick={() => {
                          props.switchPersonaRole(roleOpt);
                          setOrgMenuOpen(false);
                        }}
                        className={`flex w-full cursor-pointer items-center justify-between gap-2 rounded-control px-2.5 py-1.5 text-left text-[12px] transition-colors ${session.personaRole === roleOpt ? 'bg-brand-wash font-semibold text-brand' : 'text-fg hover:bg-surface-subtle'}`}
                      >
                        <span className="truncate">{formatPersonaRoleLabel(roleOpt)}</span>
                        <Badge variant={getRoleChipVariant(roleOpt)} size="sm">
                          {authSession.getRoleShortLabel(roleOpt)}
                        </Badge>
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-2 p-3">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={handleToggleTheme}
                  aria-label="Toggle color theme"
                  data-testid="theme-toggle-button"
                  className="w-full"
                >
                  <ui.IconRefresh />
                  <span>{themeLabel}</span>
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setOrgMenuOpen(false);
                    setChangePasswordOpen(true);
                  }}
                  data-testid="profile-change-password-button"
                  className="w-full"
                >
                  <ui.IconKey />
                  <span>Change password</span>
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setOrgMenuOpen(false);
                    void props.signOut();
                  }}
                  className="w-full"
                >
                  <ui.IconSignOut />
                  <span>{ADMIN_STRINGS.navigation.signOutButton}</span>
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
      <ChangePasswordModal
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
        onPasswordChanged={() => setPasswordSavedMessage('Your password has been updated.')}
      />
      <ToastNotification
        message={passwordSavedMessage}
        onDismiss={() => setPasswordSavedMessage(null)}
      />
    </header>
  );
}
